import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';

const Impressum = () => {
  const { language, changeLanguage, availableLanguages } = useLanguage();

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link
              to="/"
              className="text-2xl font-light tracking-[0.3em] text-white hover:text-[#B8860B] transition-colors"
              data-testid="impressum-logo"
            >
              ALKA
            </Link>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1 text-xs">
                {availableLanguages.map((lang, idx) => (
                  <React.Fragment key={lang}>
                    <button
                      onClick={() => changeLanguage(lang)}
                      className={`px-2 py-1 transition-colors ${
                        language === lang ? 'text-[#B8860B]' : 'text-white/60 hover:text-white'
                      }`}
                    >
                      {lang.toUpperCase()}
                    </button>
                    {idx < availableLanguages.length - 1 && <span className="text-white/20">|</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-[#A1A1AA] hover:text-[#B8860B] text-sm mb-8 transition-colors"
            data-testid="impressum-back"
          >
            <ArrowLeft size={16} />
            <span>{language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}</span>
          </Link>

          <h1 className="font-outfit text-4xl sm:text-5xl font-light text-white tracking-tight mb-12">
            Impressum
          </h1>

          <div className="prose prose-invert prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">Angaben gemäß § 5 TMG</h2>
              <div className="text-[#A1A1AA] space-y-2">
                <p>[Firmenname / Name]</p>
                <p>[Straße und Hausnummer]</p>
                <p>[PLZ und Ort]</p>
                <p>Deutschland</p>
              </div>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">Kontakt</h2>
              <div className="text-[#A1A1AA] space-y-2">
                <p>Telefon: +49 178 5785105</p>
                <p>E-Mail: info@alka.website</p>
                <p>Website: alka.website</p>
              </div>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">Vertreten durch</h2>
              <p className="text-[#A1A1AA]">[Name des Vertretungsberechtigten]</p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">Umsatzsteuer-ID</h2>
              <p className="text-[#A1A1AA]">
                Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
                [USt-IdNr.]
              </p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">Handelsregister</h2>
              <div className="text-[#A1A1AA] space-y-2">
                <p>Registergericht: [Amtsgericht]</p>
                <p>Registernummer: [HRB Nummer]</p>
              </div>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h2>
              <div className="text-[#A1A1AA] space-y-2">
                <p>[Name]</p>
                <p>[Adresse]</p>
              </div>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">Streitschlichtung</h2>
              <p className="text-[#A1A1AA] leading-relaxed">
                Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{' '}
                <a
                  href="https://ec.europa.eu/consumers/odr/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#B8860B] hover:underline"
                >
                  https://ec.europa.eu/consumers/odr/
                </a>
                <br /><br />
                Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
              </p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">Haftung für Inhalte</h2>
              <p className="text-[#A1A1AA] leading-relaxed">
                Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
              </p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">Haftung für Links</h2>
              <p className="text-[#A1A1AA] leading-relaxed">
                Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
              </p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">Urheberrecht</h2>
              <p className="text-[#A1A1AA] leading-relaxed">
                Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
          <p className="text-[#A1A1AA]/60 text-xs">© {new Date().getFullYear()} ALKA</p>
          <div className="flex items-center space-x-4 text-xs">
            <Link to="/impressum" className="text-[#B8860B]">Impressum</Link>
            <Link to="/datenschutz" className="text-[#A1A1AA] hover:text-[#B8860B] transition-colors">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Impressum;
